import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDependencies } from "./useDependencies";
import { useAuth } from "./useAuth";
import { useBrowserLocalStorage } from "./useBrowserLocalStorage";
import { useSlug } from "./useSlug";
import { useGetUserLink } from "./useGetUserLink";
import { useNavigate } from "react-router-dom";
import { DOCUMENTATION_AREA_ROUTES } from "../routes/Navigation";
import { HTTPError } from "src/contexts/_shared/domain/errors/HTTPError";
import { useLocale } from "./useLocale";
import { getDocumentationAreaPath } from "../routes/getDocumentationAreaPath";
import { getDocumentationAreaIdAndActorId } from "./useDocumentationAreaIdAndActorId";

const DOCUMENTATION_AREA_LOGIN_OTP_PIN_INPUT_ERROR = "documentation_area.login.otp.pin_input.error";

type MutationParams = {
  code: string;
  email: string;
};

export const useGetToken = () => {
  const navigate = useNavigate();
  const { setToken, setOTPSubmitError } = useAuth();
  const { authController, documentationAreaController, translationController } = useDependencies();
  const { linkType, slug, isMagicLink, isUserLink } = useSlug();
  const { userLink } = useGetUserLink();
  const { locale } = useLocale();
  const queryClient = useQueryClient();

  const [_, setTokenToLocalStorage] = useBrowserLocalStorage<string | null>(
    `stored_query_param.token.${linkType}.${slug}`,
    null,
  );

  const mutationFn = async ({ code, email }: MutationParams) => {
    if (!linkType) {
      throw new Error("Link type is required to get the token");
    }
    const token = await authController.getToken({
      code,
      email,
      linkType,
      linkId: userLink?.id ?? null,
    });

    try {
      const documentationAreaIds = await getDocumentationAreaIdAndActorId({
        documentationAreaController,
        token,
        slug,
        isMagicLink: Boolean(isMagicLink),
        isUserLink: Boolean(isUserLink),
      });

      const { documentationAreaId, actorId } = documentationAreaIds;
      queryClient.setQueryData(["DocumentationAreaId", slug], documentationAreaIds);

      const localeConfig = await translationController.getLocaleConfigByDocumentationAreaId(
        documentationAreaId,
        actorId,
        token,
      );
      queryClient.setQueryData(["locale-config", documentationAreaId, actorId], localeConfig);

      const resolvedLocale = localeConfig.resolvedLocale || locale;
      if (resolvedLocale) {
        window.localStorage.setItem(
          `stored_query_param.locale.${documentationAreaId}`,
          JSON.stringify(resolvedLocale),
        );
      }

      const [documentationArea, hasSignedAcceptance] = await Promise.all([
        documentationAreaController.get(token, documentationAreaId, actorId, resolvedLocale),
        documentationAreaController.getHasSignedAcceptance(token, documentationAreaId, actorId),
      ]);

      queryClient.setQueryData(
        ["DocumentationArea", documentationAreaId, actorId, resolvedLocale],
        documentationArea,
      );
      queryClient.setQueryData(
        ["getSignedAcceptance", documentationAreaId, actorId],
        hasSignedAcceptance,
      );

      return {
        token,
        path: getDocumentationAreaPath(documentationArea, hasSignedAcceptance),
      };
    } catch {
      return { token, path: DOCUMENTATION_AREA_ROUTES.HOME };
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess: ({ token, path }) => {
      setToken(token);
      setTokenToLocalStorage(token);
      navigate({ pathname: path, search: location.search }, { replace: true });
    },
    onError: (error) => {
      if (!(error instanceof HTTPError)) {
        return;
      }

      if (error.getHTTPCode().isBadRequest()) {
        setOTPSubmitError(DOCUMENTATION_AREA_LOGIN_OTP_PIN_INPUT_ERROR);
        return;
      }

      if (error.getHTTPCode().isForbidden()) {
        navigate({ pathname: DOCUMENTATION_AREA_ROUTES.LOGIN, search: location.search });
        return;
      }
    },
  });

  return { isGetTokenPending: isPending, getToken: mutate };
};
